/** Marketing i18n fragment — ucDeployment (use-cases/ai-deployment-governance). */

export const ucDeploymentEn = {
  shot: {
    label: "Deployment approval",
    alt: "A governed mission pausing for deployment approval — execution held until a person signs off, gated on a green CI run.",
  },
  meta: {
    title: "Deployment gates with human approval and live rollout status",
    description:
      "Hold risky promotions at an approval gate. Where Argo and Kubernetes are connected, FixControl shows live rollout status and, on approval, promotes a paused rollout. Failed provider actions stay visible, and every decision is recorded.",
  },
  hero: {
    eyebrow: "Deployment governance",
    title: "Deployments wait at a gate until",
    highlight: "someone on your team approves",
    subtitle:
      "A bad promotion is one of the most expensive mistakes a team can make. Promotion is a decision, and FixControl treats it like one: approval cards state what would change and the risk attached; where Argo and Kubernetes are connected, the approver also sees live rollout status. Approving promotes the paused rollout, and the decision lands in the audit trail.",
    primaryCta: "Book a demo",
    secondaryCta: "Talk to us",
    bullet1: "Human approval before promotion",
    bullet2: "Live rollout status at the gate",
    bullet3: "Failures stay visible",
  },
  how: {
    eyebrow: "How it works",
    title: "A gate between ready and promoted",
    description:
      "A release reaches the gate, someone on your team reviews it with live status in view, and promotion happens on their verdict.",
  },
  steps: {
    gate: {
      tag: "Gate opens",
      title: "A release reaches an approval gate",
      body: "When a change is ready to promote, a deployment approval card opens. It states what would be promoted and the risk attached to it.",
    },
    status: {
      tag: "Status in view",
      title: "The approver sees the live rollout",
      body: "The card shows the current state of the rollout — paused, progressing or degraded — read from Argo and Kubernetes when they are connected. The approver decides on live data.",
    },
    risk: {
      tag: "Risk gates",
      title: "Risky promotions wait for someone on your team",
      body: "Higher-risk promotions pause until someone on your team with the right role decides. The gate is where that person chooses to proceed, hold or abort.",
    },
    promote: {
      tag: "Promote",
      title: "Approval promotes the paused rollout",
      body: "A canary sits paused mid-rollout at the gate. The approver checks what Argo reports and approves; FixControl promotes the paused rollout and records the decision, with abort and rollback status visible as Argo reports it.",
    },
  },
  features: {
    eyebrow: "What you get",
    title: "Gates, live status and a record of every promotion",
    description:
      "Capabilities that depend on Argo or Kubernetes are labeled that way, on this page and in the product.",
    cards: {
      title: "Deployment approval cards",
      body: "Each promotion arrives as a card stating what would change and the risk involved. The person on your team who decides has that context in front of them.",
    },
    riskGates: {
      title: "Risk gates before promotion",
      body: "Risky promotions stop at a gate and wait for a human decision. Nothing about a risky promotion proceeds automatically.",
    },
    rolloutStatus: {
      title: "Live Argo and Kubernetes status",
      body: "FixControl reads rollout state from the connected providers and shows it at the gate. The approver sees the deployment as it is at the moment of decision.",
    },
    promoteOrRead: {
      title: "Promote on approval, or read-only",
      body: "Where Argo is configured for promotion, an approved decision resumes a paused rollout. Where it is not, FixControl reads and shows status, including abort and rollback state as Argo reports it.",
    },
    failures: {
      title: "Failed actions stay in view",
      body: "A provider action that fails shows up in the operational timeline and the outbox. A stuck or failed promotion stays visible until someone deals with it.",
    },
    audit: {
      title: "Every promotion decision on the record",
      body: "Who approved a promotion, from which channel, on what evidence and with what outcome is recorded. The decision can be reviewed after the fact.",
    },
  },
  governed: {
    eyebrow: "Production stays gated",
    title: "Promotion is a recorded human decision",
    noteTitle: "Gates, visibility and audit",
    noteBody:
      "AI does not deploy to production on its own: by default, nothing is promoted without a recorded human decision, and risky promotions are held at a gate. Where Argo is connected and configured for it, an approved decision promotes the paused rollout, with abort and rollback status shown as Argo reports it. Failed provider actions stay visible in the timeline and outbox, and each decision is recorded for audit.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Questions platform teams ask",
    q1: {
      q: "Does the AI promote to production by itself?",
      a: "No. A promotion happens only after someone on your team approves it at a gate. AI prepares the card, the status and the evidence; the verdict belongs to a person on your team.",
    },
    q2: {
      q: "What does FixControl actually do at deploy time today?",
      a: "It opens deployment approval cards, holds risky promotions behind gates, and shows Argo and Kubernetes rollout status where those are connected. Where Argo is configured for promotion, approval resumes a paused rollout.",
    },
    q3: {
      q: "What happens if a provider action fails?",
      a: "It stays visible in the operational timeline and the outbox, with retry state and dead-letter jobs you can act on. A failed promotion is not something you discover later.",
    },
    q4: {
      q: "Is Argo or Kubernetes required?",
      a: "Live rollout status and approved promotion depend on Argo and Kubernetes being connected and configured. Where they are not, FixControl shows only what it can read, and the capability labels say so.",
    },
    q5: {
      q: "Is the deployment decision recorded?",
      a: "Yes. The approver, the channel, the evidence in view and the outcome are captured in the audit trail, like any other governed decision.",
    },
    q6: {
      q: "Can a risky promotion be forced through automatically?",
      a: "No. Risky promotions are gated for a human decision by design, and the gate is where someone on your team chooses to proceed, hold or abort.",
    },
  },
  related: {
    argo: {
      label: "Argo integration",
      description: "Rollout status and gated promotion where configured.",
    },
    supportEngineering: {
      label: "Support → engineering",
      description: "Turn support tickets into governed engineering fixes.",
    },
    approvalLedger: {
      label: "AI approval ledger",
      description: "The decision record behind every approval.",
    },
  },
  cta: {
    title: "See a gated promotion",
    description:
      "Book a demo and approve a paused rollout yourself: the gate, the live status, the promotion and the audit entry it leaves.",
    primary: "Book a demo",
    secondary: "Talk to us",
  },
};

export const ucDeploymentNl = {
  shot: {
    label: "Deploymentgoedkeuring",
    alt: "Een gegoverneerde missie die pauzeert voor deploymentgoedkeuring — uitvoering wordt vastgehouden tot een mens tekent, gebonden aan een groene CI-run.",
  },
  meta: {
    title: "Deployment-poorten met menselijke goedkeuring en live rollout-status",
    description:
      "Houd risicovolle promoties tegen bij een goedkeuringspoort. Waar Argo en Kubernetes verbonden zijn, toont FixControl live rollout-status en promoot het, bij goedkeuring, een gepauzeerde rollout. Mislukte provideracties blijven zichtbaar, en elke beslissing wordt vastgelegd.",
  },
  hero: {
    eyebrow: "Deployment-governance",
    title: "Deployments wachten bij een poort tot",
    highlight: "iemand van je team goedkeurt",
    subtitle:
      "Een verkeerde promotie is een van de duurste fouten die een team kan maken. Promoten is een beslissing, en FixControl behandelt het als zodanig: goedkeuringskaarten tonen wat er zou veranderen en welk risico eraan vastzit; waar Argo en Kubernetes verbonden zijn, ziet de goedkeurder ook live rollout-status. Goedkeuren promoot de gepauzeerde rollout, en de beslissing belandt in de audittrail.",
    primaryCta: "Boek een demo",
    secondaryCta: "Neem contact op",
    bullet1: "Menselijke goedkeuring vóór promoten",
    bullet2: "Live rollout-status bij de poort",
    bullet3: "Mislukkingen blijven zichtbaar",
  },
  how: {
    eyebrow: "Hoe het werkt",
    title: "Een poort tussen klaar en gepromoot",
    description:
      "Een release bereikt de poort, iemand van je team beoordeelt het met live status in beeld, en promoten gebeurt op zijn oordeel.",
  },
  steps: {
    gate: {
      tag: "Poort opent",
      title: "Een release bereikt een goedkeuringspoort",
      body: "Wanneer een wijziging klaar is om te promoten, opent een deployment-goedkeuringskaart. Die toont wat er gepromoot zou worden en het risico dat eraan vastzit.",
    },
    status: {
      tag: "Status in beeld",
      title: "De goedkeurder ziet de live rollout",
      body: "De kaart toont de huidige staat van de rollout — gepauzeerd, vorderend of gedegradeerd — gelezen uit Argo en Kubernetes wanneer die verbonden zijn. De goedkeurder beslist op live data.",
    },
    risk: {
      tag: "Risicopoorten",
      title: "Risicovolle promoties wachten op iemand van je team",
      body: "Promoties met hoger risico pauzeren tot iemand van je team met de juiste rol beslist. De poort is waar die persoon kiest om door te gaan, vast te houden of af te breken.",
    },
    promote: {
      tag: "Promoten",
      title: "Goedkeuring promoot de gepauzeerde rollout",
      body: "Een canary staat gepauzeerd midden in de rollout bij de poort. De goedkeurder controleert wat Argo rapporteert en keurt goed; FixControl promoot de gepauzeerde rollout en legt de beslissing vast, met afbreek- en rollback-status zichtbaar zoals Argo het rapporteert.",
    },
  },
  features: {
    eyebrow: "Wat je krijgt",
    title: "Poorten, live status en een vastlegging van elke promotie",
    description:
      "Capabilities die van Argo of Kubernetes afhangen, worden als zodanig gelabeld, op deze pagina en in het product.",
    cards: {
      title: "Deployment-goedkeuringskaarten",
      body: "Elke promotie verschijnt als een kaart die aangeeft wat er zou veranderen en welk risico erbij komt. De persoon uit je team die beslist, heeft die context voor zich.",
    },
    riskGates: {
      title: "Risicopoorten vóór promoten",
      body: "Riskante promoties stoppen bij een poort en wachten op een menselijke beslissing. Aan een riskante promotie gaat niets automatisch verder.",
    },
    rolloutStatus: {
      title: "Live Argo- en Kubernetes-status",
      body: "FixControl leest rollout-status uit de verbonden providers en toont die bij de poort. De goedkeurder ziet de deployment zoals die is op het moment van beslissen.",
    },
    promoteOrRead: {
      title: "Promoten bij goedkeuring, of alleen-lezen",
      body: "Waar Argo voor promoten is geconfigureerd, hervat een goedgekeurde beslissing een gepauzeerde rollout. Waar dat niet zo is, leest en toont FixControl de status, inclusief afbreek- en rollback-status zoals Argo het rapporteert.",
    },
    failures: {
      title: "Mislukte provideracties blijven zichtbaar",
      body: "Een provideractie die mislukt, verschijnt in de operationele tijdlijn en de outbox. Een vastgelopen of mislukte promotie blijft zichtbaar tot iemand hem oppakt.",
    },
    audit: {
      title: "Elke promotiebeslissing vastgelegd",
      body: "Wie een promotie goedkeurde, vanuit welk kanaal, op welk bewijs en met welke uitkomst wordt vastgelegd. De beslissing kan achteraf worden nagekeken.",
    },
  },
  governed: {
    eyebrow: "Productie blijft gepoort",
    title: "Promoten is een vastgelegde menselijke beslissing",
    noteTitle: "Poorten, zichtbaarheid en audit",
    noteBody:
      "AI deployt niet zelf naar productie: standaard wordt er niets gepromoot zonder een vastgelegde menselijke beslissing, en risicovolle promoties worden bij een poort tegengehouden. Waar Argo verbonden en daarvoor geconfigureerd is, promoot een goedgekeurde beslissing de gepauzeerde rollout, met afbreek- en rollback-status getoond zoals Argo het rapporteert. Mislukte provideracties blijven zichtbaar in de tijdlijn en outbox, en elke beslissing wordt voor audit vastgelegd.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Vragen die platformteams stellen",
    q1: {
      q: "Promoot de AI zelf naar productie?",
      a: "Nee. Een promotie gebeurt pas nadat iemand van je team het bij een poort goedkeurt. AI bereidt de kaart, de status en het bewijs voor; het oordeel is van iemand van je eigen team.",
    },
    q2: {
      q: "Wat doet FixControl vandaag eigenlijk op het moment van deployen?",
      a: "Het opent deployment-goedkeuringskaarten, houdt riskante promoties achter poorten, en toont Argo- en Kubernetes-rollout-status waar die verbonden zijn. Waar Argo voor promoten is geconfigureerd, hervat goedkeuring een gepauzeerde rollout.",
    },
    q3: {
      q: "Wat gebeurt er als een provideractie mislukt?",
      a: "Het blijft zichtbaar in de operationele tijdlijn en de outbox, met retry-status en dead-letter-jobs waarop je kunt handelen. Een mislukte promotie is niet iets wat je later ontdekt.",
    },
    q4: {
      q: "Zijn Argo of Kubernetes vereist?",
      a: "Live rollout-status en goedgekeurde promotie zijn afhankelijk van Argo en Kubernetes die verbonden en geconfigureerd zijn. Waar dat niet zo is, toont FixControl alleen wat het kan lezen, en de capability-labels zeggen dat.",
    },
    q5: {
      q: "Wordt de deployment-beslissing vastgelegd?",
      a: "Ja. De goedkeurder, het kanaal, het bewijs in beeld en de uitkomst worden vastgelegd in de audittrail, zoals elke andere gegoverneerde beslissing.",
    },
    q6: {
      q: "Kan een riskante promotie automatisch worden doorgedrukt?",
      a: "Nee. Riskante promoties worden met opzet voor een menselijke beslissing gegated. De poort is het bewuste punt waar iemand van je team kiest om door te gaan, af te breken of vast te houden.",
    },
  },
  related: {
    argo: {
      label: "Argo-integratie",
      description: "Rollout-status en gepoorte promotie waar geconfigureerd.",
    },
    supportEngineering: {
      label: "Support → engineering",
      description: "Maak van supporttickets gegoverneerde engineeringfixes.",
    },
    approvalLedger: {
      label: "AI-goedkeuringsgrootboek",
      description: "Het beslissingsrecord achter elke goedkeuring.",
    },
  },
  cta: {
    title: "Zie een gepoorte promotie",
    description:
      "Boek een demo en keur zelf een gepauzeerde rollout goed: de poort, de live status, de promotie en de auditregel die het achterlaat.",
    primary: "Boek een demo",
    secondary: "Neem contact op",
  },
};
